import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  Typography,
  TextField,
  MenuItem,
  Button,
  Fade,
  useTheme,
  useMediaQuery,
  Paper,
  InputAdornment
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";

import Questions from "./Questions";
import ScrollBarPage from "./ScrollBarPage";

import {
  AddAnswer,
  GetAllQuestions,
  GetCousesOfInstructror,
} from "../../../api";
import ErrorAlert from "../../../commonFunctions/Alerts/ErrorAlert";

const QA = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [courseCode, setcourseCode] = useState("");
  const [cmbCourses, setcmbCourses] = useState([]);
  const [questions, setquestions] = useState([]);

  // Selected Question State
  const [questionItemCode, setquestionItemCode] = useState("");
  const [questionItemContent, setquestionItemContent] = useState("");
  const [answerContent, setanswerContent] = useState(null);

  // Answer Input
  const [answer, setanswer] = useState("");

  // --- RED THEME COLORS ---
  const colors = {
    primary: "#e53935",
    dark: "#c62828",
    light: "#ffcdd2",
    bgGradientStart: "#fafafa",
    bgGradientEnd: "#ffebee",
    text: "#b71c1c",
  };

  const styles = {
    pageWrapper: {
      background: `linear-gradient(135deg, ${colors.bgGradientStart} 0%, ${colors.bgGradientEnd} 100%)`,
      height: "100vh",
      paddingTop: "2rem",
      paddingBottom: "2rem",
      marginLeft: isSmallScreen ? "0px" : "260px",
      transition: "margin-left 0.3s ease",
      width: isSmallScreen ? "100%" : "calc(100% - 260px)",
      display: "flex",
      flexDirection: "column",
    },
    headerCard: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.dark} 100%)`,
      color: "white",
      borderRadius: "20px",
      padding: "2rem",
      marginBottom: "2rem",
      boxShadow: "0 10px 40px rgba(229, 57, 53, 0.3)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "1rem",
    },
    mainCard: {
      flex: 1,
      borderRadius: "20px",
      overflow: "hidden",
      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      display: "flex",
      maxHeight: "calc(100vh - 180px)", // Prevents overflow
    },
  };

  useEffect(() => {
    GetCousesOfInstructror(setcmbCourses);
    GetAllQuestions(courseCode, setquestions);
  }, [courseCode]);

  // Handle Filter Change
  const handleSelectQA = (e) => {
    setcourseCode(e.target.value);
    setquestionItemCode("");
    setquestionItemContent("");
    setanswerContent(null);
  };

  // Submit Answer
  const handlePublishAnswer = () => {
    if (answer === "") {
      ErrorAlert("Error", "Please Enter An Answer");
      return;
    }

    AddAnswer(
      questionItemCode,
      answer,
      setanswer,
      courseCode,
      questions,
      setquestions,
      setanswerContent,
      setquestionItemContent
    );
  };

  return (
    <Box style={styles.pageWrapper}>
      <Container maxWidth="xl" sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
        
        {/* Header */}
        <Fade in timeout={500}>
          <Box style={styles.headerCard}>
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QuestionAnswerIcon fontSize="large" /> Q & A
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                Answer questions from your students
              </Typography>
            </Box>

            {/* Filter Dropdown */}
            <Box sx={{ minWidth: 250, background: 'rgba(255,255,255,0.15)', borderRadius: 2, padding: '4px 10px' }}>
              <TextField
                select
                fullWidth
                variant="standard"
                value={courseCode}
                onChange={handleSelectQA}
                InputProps={{ disableUnderline: true, style: { color: 'white' } }}
                SelectProps={{
                  displayEmpty: true,
                  iconComponent: () => <FilterAltIcon sx={{ color: 'white' }} />
                }}
              >
                <MenuItem value=""><em>All Courses</em></MenuItem>
                {cmbCourses.map((course, index) => (
                  <MenuItem key={index} value={course.code}>
                    {course.title}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Fade>

        {/* Main Content Area */}
        <Card style={styles.mainCard}>
          <Grid container sx={{ height: "100%" }}>
            
            {/* LEFT: Questions List */}
            <Grid item xs={12} md={4} sx={{ borderRight: "1px solid #e0e0e0", height: "100%", bgcolor: "#fff" }}>
              <Questions
                setanswerContent={setanswerContent}
                questions={questions}
                setquestionItemContent={setquestionItemContent}
                setquestionItemCode={setquestionItemCode}
                activeCode={questionItemCode} // Pass active code for highlighting
              />
            </Grid>

            {/* RIGHT: Answer Area */}
            <Grid item xs={12} md={8} sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#f5f7fb" }}>
              {questionItemContent !== "" ? (
                <>
                  {/* Chat/Answer Display */}
                  <Box sx={{ flex: 1, overflowY: "auto", p: 0 }}>
                    <ScrollBarPage
                      answerContent={answerContent}
                      questionItemContent={questionItemContent}
                    />
                  </Box>

                  {/* Input Area */}
                  <Box sx={{ p: 2, bgcolor: "white", borderTop: "1px solid #e0e0e0", display: "flex", gap: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Post a public answer..."
                      variant="outlined"
                      value={answer}
                      onChange={(e) => setanswer(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "20px",
                          bgcolor: "#f5f5f5",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handlePublishAnswer}
                      sx={{
                        borderRadius: "50%",
                        minWidth: "50px",
                        width: "50px",
                        height: "50px",
                        bgcolor: colors.primary,
                        "&:hover": { bgcolor: colors.dark }
                      }}
                    >
                      <SendIcon />
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "text.secondary", opacity: 0.6 }}>
                  <QuestionAnswerIcon sx={{ fontSize: 80, mb: 2, color: "#bdbdbd" }} />
                  <Typography variant="h5" fontWeight="bold">Select a Question</Typography>
                  <Typography>Choose a question from the list to view and answer.</Typography>
                </Box>
              )}
            </Grid>

          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default QA;